/**
 * Calls Claude from the browser with the user's own key. The calls go
 * straight to Anthropic; the key is kept in the user's own Google Drive
 * (keyStore.ts), never on a server of ours.
 */
import Anthropic from '@anthropic-ai/sdk'
import { betaZodOutputFormat } from '@anthropic-ai/sdk/helpers/beta/zod'
import { BillsSchema, extractionSchema, type Bills, type Extraction } from './extraction'
import { catalog, describeForClaude, extractionIds, type Catalog } from './categories'
import { tokens, type Meter } from './usage'

/**
 * Awaits a structured-output stream. When Claude reaches max_tokens partway
 * through the JSON, the SDK's parse throws before the stop reason can be
 * read; this returns the unparsed message instead, so the caller reports the
 * length limit and still records the tokens spent.
 */
export async function finish<M>(stream: {
  finalMessage(): Promise<M>
  readonly currentMessage: Anthropic.Beta.BetaMessage | undefined
}): Promise<M | (Anthropic.Beta.BetaMessage & { parsed_output: null })> {
  try {
    return await stream.finalMessage()
  } catch (error) {
    const cut = stream.currentMessage
    if (cut?.stop_reason === 'max_tokens') return { ...cut, parsed_output: null }
    throw error
  }
}

/** One model for both jobs until measurement says a cheaper one holds up. */
export const EXTRACTION_MODEL = 'claude-opus-5-5'

/** The API's request ceiling is 32 MB; base64 grows a file by a third. */
export const MAX_FILE_BYTES = 23 * 1024 * 1024

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const
type ImageType = (typeof IMAGE_TYPES)[number]

export type DocumentInput =
  | { kind: 'pdf'; base64: string }
  | { kind: 'image'; mediaType: ImageType; base64: string }
  | { kind: 'text'; text: string }

/** How a Drive MIME type is sent to Claude, or undefined if it can't be. */
export function inputKind(mimeType: string): DocumentInput['kind'] | undefined {
  if (mimeType === 'application/pdf') return 'pdf'
  if ((IMAGE_TYPES as readonly string[]).includes(mimeType)) return 'image'
  if (mimeType.startsWith('text/') || mimeType === 'application/x-ofx' || mimeType === 'application/vnd.intu.qfx') {
    return 'text'
  }
  return undefined
}

export class ExtractionError extends Error {
  /** `retry` = leave the file for the next run; `review` = file it in Needs Review. */
  constructor(message: string, readonly disposition: 'retry' | 'review') {
    super(message)
    this.name = 'ExtractionError'
  }
}

const SYSTEM = `You read personal-finance documents for one household and extract their contents as structured data.

Rules:
- Every date is YYYY-MM-DD. Convert whatever the document prints.
- institution is the brand the account holder deals with, as printed most prominently: "Fidelity" for a Fidelity-branded card issued by Elan, not "Fidelity (Elan Financial Services)". No parentheses, slashes or issuer notes. If the document belongs to one of the known institutions you are given, use that name exactly. On a pay stub or W-2 the institution is the employer (as named on the stub), not the payroll provider or the bank the pay is deposited to.
- Never output a full account number, SSN, routing number or tax ID. For accounts give only the last 4 digits.
- List every account the document covers in accounts. A combined statement covering several accounts lists each one, and every balance, transaction and holding gives the last4 of the account it belongs to. A transfer between two listed accounts appears once on each side and is categorised "transfer".
- Transaction amounts: negative for money leaving the account holder (purchases, payments out, fees), positive for money coming in (deposits, refunds, credits). On a credit card statement a purchase is negative and a payment to the card is positive.
- Money moved between the account holder's own accounts is categorised "transfer" on both sides, whether or not the other account is on this document: a card payment or autopay on a bank statement, a move to savings or a brokerage account. Zelle or a check to another person is not a transfer.
- Balances on credit cards and loans are negative when money is owed.
- Include every transaction and holding the document lists; do not summarise or sample them.
- Where the document doesn't say, use "" for text fields and null for numbers, dates and objects. Don't infer an expense ratio, APR or cost basis that isn't printed.
- confidence reflects how sure you are about doc_type, institution, document_date and the account. Below 0.7, say why in review_reason; otherwise leave it "".
- If the document is not financial or is unreadable, use doc_type "other", explain in review_reason, and return empty lists.`

export function client(apiKey: string): Anthropic {
  // The key belongs to the person using the page; there is no server to hide it on.
  return new Anthropic({ apiKey, dangerouslyAllowBrowser: true })
}

function contentBlock(input: DocumentInput): Anthropic.Beta.BetaContentBlockParam {
  switch (input.kind) {
    case 'pdf':
      return { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: input.base64 } }
    case 'image':
      return { type: 'image', source: { type: 'base64', media_type: input.mediaType, data: input.base64 } }
    case 'text':
      return { type: 'document', source: { type: 'text', media_type: 'text/plain', data: input.text } }
  }
}

export async function extract(
  anthropic: Anthropic,
  fileName: string,
  input: DocumentInput,
  knownInstitutions: string[] = [],
  meter?: Meter,
  /** model and effort are for comparing models; the app passes only the household's categories. */
  options: { model?: string; effort?: 'low' | 'medium' | 'high'; categories?: Catalog } = {},
): Promise<Extraction> {
  const known = knownInstitutions.length
    ? `Known institutions (reuse these exact names): ${knownInstitutions.join('; ')}\n`
    : ''
  const cats = options.categories ?? catalog()
  const categories = `Categories (give the id before the =; pick the most specific that fits, else the parent's own): ${describeForClaude(cats)}\n`
  let message
  const started = performance.now()
  try {
    message = await finish(
      anthropic.beta.messages.stream({
        model: options.model ?? EXTRACTION_MODEL,
        max_tokens: 64000,
        betas: ['server-side-fallback-2026-07-01'],
        fallbacks: 'default',
        system: SYSTEM,
        output_config: { effort: options.effort ?? 'medium', format: betaZodOutputFormat(extractionSchema(extractionIds(cats))) },
        messages: [
          {
            role: 'user',
            content: [contentBlock(input), { type: 'text', text: `${known}${categories}File name: ${fileName}\n\nExtract this document.` }],
          },
        ],
      }),
    )
  } catch (error) {
    if (error instanceof Anthropic.AuthenticationError) {
      throw new ExtractionError('Claude rejected the API key', 'retry')
    }
    if (error instanceof Anthropic.BadRequestError) {
      throw new ExtractionError(`Claude could not read this file: ${error.message}`, 'review')
    }
    if (error instanceof Anthropic.APIError) {
      throw new ExtractionError(`Claude API error ${error.status ?? ''}: ${error.message}`, 'retry')
    }
    throw new ExtractionError(`Could not reach Claude: ${(error as Error).message}`, 'retry')
  }

  meter?.({ kind: 'extract', ref: fileName, model: message.model, seconds: (performance.now() - started) / 1000, ...tokens(message.usage) })
  if (message.stop_reason === 'refusal') {
    throw new ExtractionError('Claude declined to read this document', 'review')
  }
  if (message.stop_reason === 'max_tokens') {
    throw new ExtractionError('Document too long to extract in one pass', 'review')
  }
  if (!message.parsed_output) {
    throw new ExtractionError('Claude returned data that did not match the expected shape', 'review')
  }
  return message.parsed_output
}

/**
 * Re-reads only the due date, minimum payment and AutoPay from a card or loan
 * statement read before those fields were captured. Much smaller output than
 * a full extraction.
 */
export async function readBills(anthropic: Anthropic, fileName: string, input: DocumentInput, meter?: Meter): Promise<Bills> {
  const started = performance.now()
  const message = await finish(
    anthropic.beta.messages.stream({
      model: EXTRACTION_MODEL,
      max_tokens: 4000,
      betas: ['server-side-fallback-2026-07-01'],
      fallbacks: 'default',
      system: SYSTEM,
      output_config: { effort: 'low', format: betaZodOutputFormat(BillsSchema) },
      messages: [
        {
          role: 'user',
          content: [
            contentBlock(input),
            { type: 'text', text: `File name: ${fileName}\n\nFor each credit card or loan on this statement, give the payment due date, the minimum or amount due, and whether AutoPay is on.` },
          ],
        },
      ],
    }),
  )
  meter?.({ kind: 'extract', ref: `${fileName} (due dates)`, model: message.model, seconds: (performance.now() - started) / 1000, ...tokens(message.usage) })
  if (!message.parsed_output) throw new Error(`Claude could not read the due date from ${fileName}`)
  return message.parsed_output
}
